import { Alert, Button, Card, Form, Input, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationCampusSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  title: string;
  session_id: string;
  campus_id?: string;
  summary?: string;
};

export default function UdiseRunFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.udise.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/udise/runs', {
        title: values.title,
        session_id: values.session_id,
        campus_id: values.campus_id || null,
        summary: values.summary,
      }),
    onSuccess: (res) => {
      message.success(t('udiseRunCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/udise/runs/${id}` : '/education/udise/runs');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newUdiseRun')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newUdiseRun')}
      description={t('udiseRunsIntro')}
      extra={<Button onClick={() => navigate('/education/udise/runs')}>{t('udiseRuns')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('udiseReadinessHint')} />
        <Form layout="vertical" onFinish={(values) => create.mutate(values)}>
          <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect />
          </Form.Item>
          <Form.Item name="campus_id" label={t('campusOptional')}>
            <EducationCampusSelect allowClear />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
